import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getKieModelConfig } from './utils';
import { kieApiPaths, kieStatusMapping } from './config';
import type { KieCreateResponse, KieTaskResponse } from './types';
import { getValueByPath, isStatusMatch } from '../tools';

// 创建提供商实例
const provider = new BaseVideoProvider({
    baseUrlEnv: 'KIE_AI_BASE_URL',
    apiKeyEnv: 'KIE_AI_API_KEY',
    debugEnv: 'KIE_AI_DEBUG',
    providerName: 'KIE',
});


// 实现适配器
const adapter: VideoProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 获取模型配置
        const apiPath = kieApiPaths.createTask;
        // 构建输入参数（可能是异步构建）
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 返回请求配置
        return {
            url: `${context.baseUrl}${apiPath}`,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
            body: payload,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: KieCreateResponse): CreateTaskResult {
        // 获取任务 ID
        const taskId = getValueByPath(data, kieStatusMapping.taskIdPath);
        if (!taskId) {
            throw new Error(`Failed to get task id from response${data.msg ? `: ${data.msg}` : ''}`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 返回请求配置
        return {
            url: (taskId) => `${context.baseUrl}${kieApiPaths.pollTask}?taskId=${taskId}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: KieTaskResponse): PollTaskResult {
        // 获取任务状态
        const state = getValueByPath(data, kieStatusMapping.statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }
        // 1. 判断**成功**状态
        if (isStatusMatch(state, kieStatusMapping.success)) {
            // 获取结果字符串
            const resultJsonStr = getValueByPath(data, kieStatusMapping.videoUrlPath);
            // 视频 URL 不存在或不是字符串则视为**失败**状态
            if (!resultJsonStr || typeof resultJsonStr !== 'string') {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing resultJson in response',
                    raw: data,
                };
            }
            // 解析结果 JSON (JSON to Object)
            let resultJson: { resultUrls: string[] };
            try {
                resultJson = JSON.parse(resultJsonStr);
            } catch (e) {
                // 解析失败则视为**失败**状态
                return {
                    status: TaskStatus.FAILED,
                    error: 'Invalid resultJson format',
                    raw: data,
                };
            }
            // 获取视频 URL
            const videoUrl = Array.isArray(resultJson.resultUrls) ? resultJson.resultUrls[0] : undefined;
            // 视频 URL 不存在则视为**失败**状态
            if (!videoUrl) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing video URL in result',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                videoUrl,
                raw: data,
            };
        }

        // 2. 判断**失败**状态
        if (isStatusMatch(state, kieStatusMapping.failed)) {
            const error = getValueByPath(data, kieStatusMapping.errorPath);
            // 返回**失败**状态
            return {
                status: TaskStatus.FAILED,
                error: String(error) || 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, kieStatusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 判断**未知**状态 (不在配置范围内)
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const kie = (modelId: string): VideoModel => {
    const config = getKieModelConfig(modelId);
    return provider.createVideoModel(modelId, adapter);
};